import type { FC } from 'react';

import { useLinkComponent } from '@eneco/link';
import { ButtonLink, Stack, Stretch, TextLink } from '@eneco/sparky';
import type { ContentCardRendering } from '@sitecore/types/manual/ContentCard';

type ButtonLinkField = ContentCardRendering['fields']['buttonLink'];
type TextLinkField = ContentCardRendering['fields']['link'];

const hasRenderableLink = (field?: { editable?: string; value?: { href?: string; text?: string } }) =>
  Boolean((field?.value?.href && field.value.text) || field?.editable);

/** Primary button CTA shared by ContentCard and campaign variants. */
export const ContentCardButtonLink: FC<{ buttonLink?: ButtonLinkField }> = ({ buttonLink }) => {
  const Link = useLinkComponent();

  if (!hasRenderableLink(buttonLink) || !buttonLink) return null;

  return (
    <Stack.Item>
      <Stretch width={{ initial: true, md: false }}>
        <Link editable={buttonLink.editable} linkType={buttonLink.value.linktype} linkValue={buttonLink.value}>
          <ButtonLink target={buttonLink.value.target}>{buttonLink.value.text}</ButtonLink>
        </Link>
      </Stretch>
    </Stack.Item>
  );
};

/** Secondary text CTA shared by ContentCard and campaign variants. */
export const ContentCardTextLink: FC<{
  link?: TextLinkField;
  /** When true, uses low-emphasis on-background styling (ContentCard with backgroundColor). */
  hasBackgroundColor?: boolean;
}> = ({ link, hasBackgroundColor = false }) => {
  const Link = useLinkComponent();

  if (!hasRenderableLink(link) || !link) return null;

  return (
    <Stack.Item>
      <Link editable={link.editable} linkType={link.value.linktype} linkValue={link.value}>
        <TextLink
          {...(hasBackgroundColor
            ? { color: 'textOnBackgroundVarOne', emphasis: 'low' as const }
            : { emphasis: 'high' as const })}
          target={link.value.target}>
          {link.value.text}
        </TextLink>
      </Link>
    </Stack.Item>
  );
};
